/* eslint-disable @typescript-eslint/no-explicit-any */
import { Tooltip } from "@material-ui/core";
import Icon from "@material-ui/core/Icon";
import { makeStyles } from "@material-ui/core/styles";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { useQueryClient } from "@tanstack/react-query";
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import Card from "components/UI/Card/Card.js";
import CardBody from "components/UI/Card/CardBody.js";
import CardHeader from "components/UI/Card/CardHeader.js";
import CardIcon from "components/UI/Card/CardIcon.js";
import ButtonTS from "components/Forms/CustomButtons/ButtonTS";
import PaginatedTable from "components/Tables/ReactTable/ManualPaginatedTable";
import { WorkItemProfiles as Profiles } from "helpers/profileEnum";
import { useSubscription } from "store/auth/auth-store";
import React from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";
import { PaginatedResult } from "services/@types";
import {
  prefetchData,
  useDashboardAtvsRequiringActionByRole,
} from "services/input-core/dashboard/get-dashboard-atvs-requiring-action-by-role";
import { ActivityItemWorker } from "services/input-core/work-items/@types";
import { ProfileTab } from "enums/ProfileTabs";
import { formatDateIfValid, getFilters, getSorting } from "utils/functions";
import { InfoSharp } from "@material-ui/icons";
import { Box } from "@mui/material";
import { format } from "date-fns";

const styleAny: any = styles;
const useStyles = makeStyles(styleAny);

interface StatCardProps {
  gridSize?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
  };
  color: "warning" | "success" | "danger" | "info" | "primary";
  icon: string;
}

function ActivitiesRequiringBuyerActionCard({
  color,
  icon,
}: StatCardProps): JSX.Element {
  const queryClient = useQueryClient();
  const { subscription } = useSubscription();
  const classes = useStyles();
  const { t } = useTranslation("dashboard");

  const [tableData, setTableData] = React.useState<any>({
    pageSize: 5,
    pageIndex: 0,
    columnFilters: [],
    sorting: [
      {
        id: "modifiedAt",
        desc: false,
      },
    ],
  });

  const columns = [
    {
      header: t("activity"),
      accessorKey: "activityItemCode",
      cell: ({ row }: any) => (
        <>
          {row.original.activityItemDescription ? (
            <Tooltip
              placement="top"
              title={row.original.activityItemDescription}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                {row.original.activityItemCode}{" "}
                <InfoSharp fontSize="small" color="disabled" />
              </Box>
            </Tooltip>
          ) : (
            <div>{row.original.activityItemCode}</div>
          )}
        </>
      ),
      enableColumnFilter: true,
    },
    {
      header: t("action"),
      accessorKey: "activityItemAction",
      enableColumnFilter: true,
    },
    {
      header: t("created-at"),
      accessorKey: "activityItemCreateDate",
      enableColumnFilter: false,
    },
    {
      header: t("ID"),
      accessorKey: "fieldglassId",
      enableColumnFilter: true,
    },
    {
      header: t("entity"),
      accessorKey: "entityType",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <>
          {row.original.entityName ? (
            <Tooltip placement="top" title={row.original.entityName}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                {row.original.entityType}{" "}
                <InfoSharp fontSize="small" color="disabled" />
              </Box>
            </Tooltip>
          ) : (
            <div>{row.original.entityType}</div>
          )}
        </>
      ),
    },
    {
      header: t("profile-name"),
      accessorKey: "ownerName",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: t("supplier-name"),
      accessorKey: "supplierName",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: t("modified-at"),
      accessorKey: "modifiedAt",
      enableColumnFilter: false,
      cell: (info: any) => {
        const value = info?.getValue();
        return formatDateIfValid(value);
      },
    },
    {
      header: t("actions"),
      accessorKey: "actions",
      cell: ({ row }: any) => (
        <div className="actions-right">
          <Tooltip placement="top" title="View">
            <Link
              to={
                `/app/${Profiles[row.original.profile as never]}/profile?id=${
                  row.original.fieldglassId
                }` +
                (row.original?.activityItemDueDate?.split("/")[1] !== undefined
                  ? `&month=${row.original?.activityItemDueDate?.split("/")[1]}`
                  : "") +
                (row.original?.activityItemDueDate?.split("/")[2] !== undefined
                  ? `&year=${row.original?.activityItemDueDate?.split("/")[2]}`
                  : "") +
                `&tab=${ProfileTab.ACTIVITY_ITEMS}&activity=${row.original.activityItemCode}`
              }
            >
              <ButtonTS
                round
                justIcon
                size="sm"
                color="info"
                key={new Date().getTime()}
              >
                <ArrowForwardIosIcon />
              </ButtonTS>
            </Link>
          </Tooltip>
        </div>
      ),
      enableColumnFilter: false,
    },
  ];

  const { data, isLoading, isFetching } = useDashboardAtvsRequiringActionByRole(
    "buyer",
    {
      "page[size]": tableData.pageSize,
      "page[index]": tableData.pageIndex,
      ...getFilters(tableData.columnFilters),
      ...getSorting(tableData.sorting),
    },
    {
      staleTime: 0,
      onSuccess: (data: PaginatedResult<ActivityItemWorker>) => {
        // prefetchData(
        //   queryClient,
        //   "buyer",
        //   {
        //     "page[size]": tableData.pageSize,
        //     "page[index]": tableData.pageIndex + 1,
        //     ...getFilters(tableData.columnFilters),
        //     ...getSorting(tableData.sorting),
        //   },
        //   subscription?.subscriptionId,
        // );
        // prefetchData(
        //   queryClient,
        //   "buyer",
        //   {
        //     "page[size]": tableData.pageSize,
        //     "page[index]":
        //       tableData.pageIndex > 0 ? tableData.pageIndex - 1 : 0,
        //     ...getFilters(tableData.columnFilters),
        //     ...getSorting(tableData.sorting),
        //   },
        //   subscription?.subscriptionId,
        // );
        setTableData((state: any) => ({
          ...state,
          pageSize: data.perPage,
          pageIndex: data.toSkip / data.perPage,
        }));
      },
    },
  );

  return (
    <Card>
      <CardHeader color={color} icon>
        <CardIcon color={color}>
          <Icon>{icon || "search"}</Icon>
        </CardIcon>
        <h4 className={classes.cardIconTitle}>
          {t("activities-requiring-your-action")}
        </h4>
      </CardHeader>
      <CardBody>
        {isLoading || isFetching ? (
          <Skeleton />
        ) : (
          <PaginatedTable
            id="activities-requiring-buyer-action-table"
            columns={columns}
            data={data?.result || []}
            tableState={tableData}
            pageCount={data?.totalPages}
            setTableState={setTableData}
          />
        )}
      </CardBody>
    </Card>
  );
}
export default ActivitiesRequiringBuyerActionCard;
